import React, { ReactNode, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { ColDef } from "@ag-grid-community/core";
import { AgGridReactProps } from "@ag-grid-community/react/lib/agGridReact";

import PermissionName from "@mapmycustomers/shared/enum/PermissionName";
import {
  AnyEntity,
  EntityTypesSupportedColumnSelection,
} from "@mapmycustomers/shared/types/entity";
import { Modal } from "@mapmycustomers/ui";

import AgGridListViewProvider from "@app/component/view/ListView/AgGridListViewProvider";
import AgGridListViewSection from "@app/component/view/ListView/AgGridListViewSection";
import AgGridListViewPagination from "@app/component/view/ListView/components/Pagination/AgGridListViewPagination";
import { DrillDownListFetcherPayload } from "@app/scene/dashboard/types/DrillDownListFetcher";
import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import { hasPermission } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import useHiddenChat from "@app/util/useHiddenChat";

import Actions from "./components/Actions";
import Grid from "./components/Grid";
import styles from "./DrillDownModal.module.scss";

export interface DrillDownModalProps<VS extends DrillDownViewState = DrillDownViewState> {
  canDownload: boolean;
  context?: AgGridReactProps["context"];
  entityType: EntityTypesSupportedColumnSelection;
  gridFrameworkComponents?: AgGridReactProps["frameworkComponents"];
  loading?: boolean;
  onActivityTypeView?: (entity: AnyEntity, element: HTMLElement) => void;
  onEdit?: () => void;
  onExportDrillDownData?: () => void;
  onFetchDrillDownData: (payload: DrillDownListFetcherPayload<AnyEntity>) => void;
  onHide: () => void;
  onRecordView?: (entity: AnyEntity) => void;
  subtitle?: ReactNode;
  title: ReactNode;
  totalFilteredRecords: number;
  viewState: VS;
}

const DrillDownModal = <VS extends DrillDownViewState>({
  canDownload,
  context,
  entityType,
  gridFrameworkComponents,
  loading,
  onActivityTypeView,
  onEdit,
  onExportDrillDownData,
  onFetchDrillDownData,
  onHide,
  onRecordView,
  subtitle,
  title,
  totalFilteredRecords,
  viewState,
}: DrillDownModalProps<VS>) => {
  const analytics = useExtendedAnalytics("DrillDown", useAnalytics());
  useHiddenChat();

  const columnDefs = useMemo(
    () =>
      viewState.columns
        .filter(({ field }) => field.isReadable || field.isSystemRequired)
        .map<ColDef>(({ field, pinned, visible, width }) => ({
          ...(field.gridProperties(false) ?? {}),
          hide: !visible,
          pinned,
          width,
        })),
    [viewState.columns]
  );

  const handleEdit = useCallback(() => {
    analytics.clicked(["Edit"]);
    onHide();
    onEdit?.();
  }, [onHide, onEdit, analytics]);

  const handleExportDrillDownData = useCallback(() => {
    analytics.clicked(["Export"]);
    onExportDrillDownData?.();
  }, [analytics, onExportDrillDownData]);

  return (
    <AgGridListViewProvider<AnyEntity>
      columnDefs={columnDefs}
      entityType={entityType}
      fieldModel={getFieldModelByEntityType(entityType)}
      key={entityType}
      listFetcher={onFetchDrillDownData}
      listViewState={viewState}
    >
      <Modal
        actions={
          <Actions
            onEdit={onEdit ? handleEdit : undefined}
            onExport={onExportDrillDownData && canDownload ? handleExportDrillDownData : undefined}
          />
        }
        centered
        className={styles.modal}
        closable
        footer={null}
        onCancel={onHide}
        open
        scrollable
        subtitle={subtitle}
        title={title}
      >
        <AgGridListViewSection>
          <Grid
            context={context}
            frameworkComponents={gridFrameworkComponents}
            loading={loading}
            onActivityTypeView={onActivityTypeView}
            onRecordView={onRecordView}
            totalFilteredRecords={totalFilteredRecords}
          />
          <AgGridListViewPagination
            className={styles.pagination}
            showSizeChanger
            totalRecords={totalFilteredRecords}
          />
        </AgGridListViewSection>
      </Modal>
    </AgGridListViewProvider>
  );
};

const mapStateToProps = (state: RootState) => ({
  canDownload: hasPermission(state)(PermissionName.EXPORT_DOWNLOAD),
});

export default connect(mapStateToProps)(DrillDownModal);
